// ============================================================
// MANIC MEETER — Main Game Loop (Browser Edition)
// ============================================================

import {
  PlayerRole, ActionType, SpecialEventType,
  TURN_TIMEOUT_MS, DELAY_COLLEAGUE_SPEAK, DELAY_BETWEEN_ROUNDS,
  DELAY_RESULT_SHOW, DELAY_MEETING_INTRO, PLAYER_ROLE_STARS,
  GameState, PowerupId, StatUpgradeId,
  IMPOSTOR_POINTS_PER_DAY, IMPOSTOR_POINTS_BONUS_NO_SLEEP,
  ActivePowerup, DailyPerk, RandomEncounter, EncounterChoice,
  AchievementId, MeetingType, SessionStats,
  VendorEncounter, WeekendEventDef,
  EndingType, PositiveEvent, POSITIVE_EVENT_SPECS,
  PermanentUnlockId, MIN_WAGE,
} from "./types";
import {
  achievementDef, achievementDefs, dailyPerks, endings, meetingTypeLabel, playerRoleLabel,
  powerupDef, powerupDefs, vendorEncounters, weekendEventLabel, weekendEvents,
} from "./content";
import { C, getLocale, onLocaleChange, otherLocale, setLocale } from "./i18n";
import { applyStaticText } from "./static-text";
import {
  createPlayer, createGameState, startNextMeeting, advanceDay,
  isPlayerTurn, getCurrentSlot, checkIfPlayerAddressed,
  checkDirectionChange, advanceToNextSpeaker, processProcrastinate,
  processUnpromptedAction, applyColleagueTurn, processQuestionAnswer,
  checkFellAsleep, checkGameOver, completeMeeting, rollForSpecialEvent,
  tickPowerups, getStatEffect, createDayStats, getPerkModifiers,
  checkAndAwardAchievements, updateSessionStatsAfterMeeting, trackColleagueFatigue,
  createAchievementTracker, createSessionStats,
  updateRelationship, getRelationshipTag,
  generateBingoCard, checkBingoGoals, isBingoComplete,
  determineEnding, rollHomeoffice,
  playSoundEffect, getMaxFatigueWithPerks, updatePeakSalary,
} from "./game";
import {
  generateAddressPhrase, getRandomQuestion, getRandomBossQuestion,
  getRandomBrainstorming, getRandomSimonSays,
  getRandomBuzzwordBingo, getRandomEmailSortieren, getRandomKalenderTetris,
  getRandomCodeReview, getRandomKiHalluzination, type AddressMethod,
  getRandomFlavor, getDirectionChangeQuote, getRandomEncounter,
} from "./events";
import {
  initRenderer, showScreen, updateStatusBar, showMeetingHeader,
  updateMeetingMeta, renderParticipants, addLogEntry, clearLog,
  clearActionPanel, getActionPanel, showGameOver, showDaySummary,
  showNewDay, showPrepPhase, getNeonClass, showMeetingProgress, clearMeetingProgress,
  showSleepingAnimation, showCaughtAnimation, showGameOverAnimation,
  screenShake, createProgressBar, getPortrait,
  showDaySummaryWithCharts, showAchievementProgress,
  renderBingoCard, showWeekendEvent, initSoundToggle,
  updateInfoPanel,
} from "./renderer";
import {
  waitForChoice, waitForCharByChar, waitForSequence, waitForContinue,
  waitForBuzzwordBingo, waitForEmailSort, waitForKalenderTetris,
  waitForCodeReview, waitForKiHalluzination,
} from "./input";
import {
  initAuth, ensureGuestSession, showLoginModal, showRegisterModal,
  showHighscoresModal, AuthResult,
} from "./auth-ui";
import { saveProfile, submitHighscore, getHighscores, isLoggedIn, HighscoreCategory, getProfile } from "./api";
import { loadMeta, saveMeta, awardPoints, setActiveRole, saveGameState, loadGameState, clearGameState, unlockEnding } from "./persistence";
import { showShop, showPowerupSelect, showVendorShop } from "./shop";

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ---- Positive Random Events ----

function positiveEvents(): PositiveEvent[] {
  const text = C().positiveEvents;
  return POSITIVE_EVENT_SPECS.map((spec) => ({ ...spec, ...text[spec.id] }));
}

// ---- Daily Perk & Encounter Helpers ----

function rollDailyPerk(state: GameState): DailyPerk | null {
  // 25% chance per day to get a random perk (positive or negative)
  if (Math.random() > 0.25) return null;
  // Filter out perks already held
  const available = dailyPerks().filter((p) => !state.activePerks.includes(p.id));
  if (available.length === 0) return null;
  return pick(available);
}

function rollAttest(): boolean {
  return Math.random() < 0.005; // < 0.5%
}

async function runEncounter(state: GameState, panel: HTMLElement): Promise<void> {
  const encounter = getRandomEncounter();
  state.daysSinceLastEncounter = 0;
  clearLog();
  addLogEntry(`${encounter.emoji} <strong>${encounter.name}</strong>`, "warning");
  addLogEntry(encounter.intro, "flavor");
  await delay(800);
  const choices = encounter.choices.map((c, i) => ({
    label: c.label,
    value: String(i),
    className: "answer",
  }));
  const picked = await waitForChoice(choices, panel, 15000);
  clearActionPanel();
  const choiceIdx = picked !== null ? parseInt(picked) : 0;
  const choice = encounter.choices[choiceIdx];
  // Check for alternate outcome
  let msg = choice.message;
  let effects = choice.effects;
  if (choice.alternateChance && choice.alternateMessage && choice.alternateEffects && Math.random() < choice.alternateChance) {
    msg = choice.alternateMessage;
    effects = choice.alternateEffects;
  }
  addLogEntry(msg, "normal");
  // Apply effects
  if (effects.fatigue) {
    state.player.fatigue = Math.max(0, Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + effects.fatigue));
    const sign = effects.fatigue > 0 ? "+" : "";
    addLogEntry(C().ui.encounter.fatigueDelta(sign, effects.fatigue), effects.fatigue < 0 ? "success" : "danger");
  }
  if (effects.salary) {
    state.player.salary += effects.salary;
    const sign = effects.salary > 0 ? "+" : "";
    addLogEntry(C().ui.encounter.salaryDelta(sign, effects.salary), effects.salary > 0 ? "success" : "danger");
  }
  if (effects.addRandomPositivePerk) {
    const available = dailyPerks().filter((p) => p.isPositive && !state.activePerks.includes(p.id));
    if (available.length > 0) {
      const p = pick(available);
      state.activePerks.push(p.id);
      addLogEntry(C().ui.encounter.newPerk(p.emoji, p.name, p.description), "success");
    }
  }
  if (effects.addRandomNegativePerk) {
    const available = dailyPerks().filter((p) => !p.isPositive && !state.activePerks.includes(p.id));
    if (available.length > 0) {
      const p = pick(available);
      state.activePerks.push(p.id);
      addLogEntry(C().ui.encounter.newPerk(p.emoji, p.name, p.description), "danger");
    }
  }
  if (effects.removeNegativePerk) {
    const negPerks = state.activePerks.filter((id) => {
      const def = dailyPerks().find((p) => p.id === id);
      return def && !def.isPositive;
    });
    if (negPerks.length > 0) {
      const removeId = pick(negPerks);
      state.activePerks = state.activePerks.filter((id) => id !== removeId);
      const def = dailyPerks().find((p) => p.id === removeId)!;
      addLogEntry(C().ui.encounter.perkRemoved(def.name), "success");
    }
  }
  if (effects.dropRandomPowerup) {
    const droppable = powerupDefs().filter((p) => p.usableInMeeting || p.usableInPrep);
    if (droppable.length > 0) {
      const pw = pick(droppable);
      state.meta.powerupInventory[pw.id] = (state.meta.powerupInventory[pw.id] || 0) + 1;
      saveMeta(state.meta);
      playSoundEffect("powerUp", state.soundEnabled);
      addLogEntry(C().ui.encounter.powerupFound(pw.name, pw.description), "success");
    }
  }
  updateStatusBar(state.player, state.meta.impostorPoints);
  updateInfoPanel(state);
}

// ---- Cookie Banner ----

function initCookieBanner(): void {
  if (localStorage.getItem("manic-meeter-cookies") !== null) return;
  const banner = document.getElementById("cookie-banner");
  if (!banner) return;
  banner.style.display = "flex";

  document.getElementById("cookie-accept")?.addEventListener("click", () => {
    localStorage.setItem("manic-meeter-cookies", "all");
    banner.style.display = "none";
  });
  document.getElementById("cookie-decline")?.addEventListener("click", () => {
    localStorage.setItem("manic-meeter-cookies", "essential");
    banner.style.display = "none";
  });
}

// ---- Auth Status Bar ----

let currentAuth: AuthResult = { loggedIn: false, isGuest: false };

function updateAuthStatus(): void {
  const el = document.getElementById("auth-status");
  if (!el) return;
  if (currentAuth.loggedIn && !currentAuth.isGuest) {
    el.innerHTML = `${C().ui.authStatus.loggedIn(currentAuth.username || "User")} · <a href="#" id="btn-logout">${C().ui.authStatus.logout}</a>`;
    document.getElementById("btn-logout")?.addEventListener("click", (e) => {
      e.preventDefault();
      import("./api").then(api => { api.logout(); });
      currentAuth = { loggedIn: false, isGuest: false };
      updateAuthStatus();
    });
    // Hide login/register if fully registered
    const loginBtn = document.getElementById("btn-login");
    const regBtn = document.getElementById("btn-register");
    if (loginBtn) loginBtn.style.display = "none";
    if (regBtn) regBtn.style.display = "none";
  } else if (currentAuth.loggedIn && currentAuth.isGuest) {
    el.innerHTML = C().ui.authStatus.guest;
    const loginBtn = document.getElementById("btn-login");
    const regBtn = document.getElementById("btn-register");
    if (loginBtn) loginBtn.style.display = "";
    if (regBtn) regBtn.style.display = "";
  } else {
    el.innerHTML = C().ui.authStatus.anonymous;
    const loginBtn = document.getElementById("btn-login");
    const regBtn = document.getElementById("btn-register");
    if (loginBtn) loginBtn.style.display = "";
    if (regBtn) regBtn.style.display = "";
  }
}

// Track loaded achievements for start screen display
let startScreenAchievements = new Set<AchievementId>();

function setupAuthButtons(): void {
  document.getElementById("btn-login")?.addEventListener("click", async () => {
    const res = await showLoginModal();
    if (res.loggedIn) { currentAuth = res; updateAuthStatus(); }
  });
  document.getElementById("btn-register")?.addEventListener("click", async () => {
    const res = await showRegisterModal();
    if (res.loggedIn) { currentAuth = res; updateAuthStatus(); }
  });
  document.getElementById("btn-highscores")?.addEventListener("click", async () => {
    const res = await getHighscores("score");
    await showHighscoresModal(res.ok && res.data ? res.data : [], async (cat) => {
      const r = await getHighscores(cat as HighscoreCategory);
      return r.ok && r.data ? r.data : [];
    });
  });
  document.getElementById("btn-achievements")?.addEventListener("click", () => {
    showAchievementProgress(startScreenAchievements);
  });
}

// ---- Save Game Progress ----

async function saveGameProgress(state: GameState): Promise<void> {
  if (!isLoggedIn()) return;
  try {
    await saveProfile({
      player_name: state.player.name,
      player_role: state.player.role,
      salary: state.player.salary,
      fatigue: state.player.fatigue,
      reputation: state.player.reputation,
      meetings_survived: state.player.meetingsSurvived,
      days_survived: state.player.daysSurvived,
      achievements: Array.from(state.achievements),
      stats: state.sessionStats as unknown as Record<string, unknown>,
    });
  } catch {
    // Silent fail — don't interrupt gameplay
  }
}

async function submitGameHighscore(state: GameState): Promise<void> {
  if (!isLoggedIn()) return;
  try {
    await submitHighscore({
      player_name: state.player.name,
      player_role: state.player.role,
      days_survived: state.player.daysSurvived,
      meetings_survived: state.player.meetingsSurvived,
      final_salary: state.peakSalary,
      longest_meeting: state.sessionStats.longestMeetingSurvived,
    });
  } catch {
    // Silent fail
  }
}

// ---- Character Creation ----

function setupCharacterCreation(): Promise<{ name: string; role: PlayerRole }> {
  return new Promise((resolve) => {
    showScreen("start-screen");

    const nameInput = document.getElementById("player-name") as HTMLInputElement;
    const roleSelect = document.getElementById("player-role") as HTMLSelectElement;
    const startBtn = document.getElementById("start-btn") as HTMLButtonElement;

    populateRoleSelect();

    startBtn.addEventListener("click", () => {
      const rawName = nameInput.value.trim().substring(0, 30).replace(/[<>&"'/]/g, "");
      const name = rawName || C().ui.start.anonymousName;
      const role = roleSelect.value as PlayerRole;
      localStorage.setItem("manic_lastRole", role);

      // Show instructions screen before starting
      showScreen("instructions-screen");
      const okBtn = document.getElementById("instructions-ok-btn")!;
      okBtn.addEventListener("click", () => {
        resolve({ name, role });
      }, { once: true });
    }, { once: true });
  });
}

// ---- Special Events ----

async function handleSpecialEvent(
  eventType: SpecialEventType,
  state: GameState,
  panel: HTMLElement,
): Promise<void> {
  if (eventType === SpecialEventType.Question) {
    const q = getRandomQuestion();
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.question, q.prompt), "warning");
    const choices = q.answers.map((a, i) => ({
      label: `${i + 1}. ${a}`,
      value: String(i),
      className: "answer",
    }));
    const mods = getPerkModifiers(state);
    const answer = await waitForChoice(choices, panel, Math.round(TURN_TIMEOUT_MS * mods.minigameTimeMult), `❓ ${q.prompt}`);
    clearActionPanel();
    let correct = false;
    if (answer !== null) {
      correct = parseInt(answer) === q.correctIndex;
      addLogEntry(C().ui.actions.yourAnswer(q.answers[parseInt(answer)]), "normal");
    } else {
      addLogEntry(C().ui.actions.noAnswer, "warning");
    }
    const result = processQuestionAnswer(state, correct);
    if (correct) { state.dayStats.bingoQuestionCorrect = true; playSoundEffect("minigameWin", state.soundEnabled); }
    else { playSoundEffect("minigameFail", state.soundEnabled); }
    addLogEntry(result.message, correct ? "success" : "danger");

  } else if (eventType === SpecialEventType.Brainstorming) {
    const challenge = getRandomBrainstorming();
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.brainstorming, challenge.context), "warning");
    addLogEntry(C().ui.minigames.typeLetterByLetter(challenge.word), "warning");
    const progressBar = createProgressBar(panel, challenge.word.length);
    const mods = getPerkModifiers(state);
    const success = await waitForCharByChar(challenge.word, panel, Math.round(challenge.timeMs * mods.minigameTimeMult), progressBar.update);
    progressBar.remove();
    if (success) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.brainstormWin, "success");
      state.player.fatigue = Math.max(0, state.player.fatigue - 10);
    } else {
      state.dayStats.minigamesLost++;
      playSoundEffect("minigameFail", state.soundEnabled);
      addLogEntry(C().ui.outcomes.brainstormFail, "danger");
      if (state.currentMeeting) state.currentMeeting.roundsRemaining += 1;
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 5);
    }

  } else if (eventType === SpecialEventType.SimonSays) {
    const challenge = getRandomSimonSays();
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.teamBuilding, challenge.context), "warning");
    addLogEntry(C().ui.minigames.repeatSequence, "warning");
    const progressBar = createProgressBar(panel, challenge.sequence.length);
    const mods = getPerkModifiers(state);
    const correct = await waitForSequence(challenge.sequence, panel, Math.round(challenge.timeMs * mods.minigameTimeMult), progressBar.update);
    progressBar.remove();
    if (correct) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.simonWin, "success");
      state.player.fatigue = Math.max(0, state.player.fatigue - 8);
    } else {
      state.dayStats.minigamesLost++;
      playSoundEffect("minigameFail", state.soundEnabled);
      addLogEntry(C().ui.outcomes.simonFail, "danger");
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 5);
    }

  } else if (eventType === SpecialEventType.BuzzwordBingo) {
    const challenge = getRandomBuzzwordBingo();
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.buzzwordBingo, challenge.context), "warning");
    addLogEntry(C().ui.minigames.memoriseOrder, "warning");
    const progressBar = createProgressBar(panel, challenge.words.length);
    const mods = getPerkModifiers(state);
    const success = await waitForBuzzwordBingo(challenge.words, challenge.correctOrder, panel, Math.round(challenge.timeMs * mods.minigameTimeMult), progressBar.update);
    progressBar.remove();
    if (success) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.bingoWin, "success");
      state.player.fatigue = Math.max(0, state.player.fatigue - 12);
    } else {
      state.dayStats.minigamesLost++;
      playSoundEffect("minigameFail", state.soundEnabled);
      addLogEntry(C().ui.outcomes.bingoFail, "danger");
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 5);
    }

  } else if (eventType === SpecialEventType.EmailSortieren) {
    const challenge = getRandomEmailSortieren();
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.emailSorting, challenge.context), "warning");
    addLogEntry(C().ui.minigames.sortEmails, "warning");
    const mods = getPerkModifiers(state);
    const correctCount = await waitForEmailSort(challenge.emails, panel, Math.round(challenge.timeMs * mods.minigameTimeMult));
    const total = challenge.emails.length;
    if (correctCount === total) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.emailPerfect(total), "success");
      state.player.fatigue = Math.max(0, state.player.fatigue - 15);
    } else if (correctCount > 0) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.emailPartial(correctCount, total), "warning");
      state.player.fatigue = Math.max(0, state.player.fatigue - 5);
    } else {
      state.dayStats.minigamesLost++;
      playSoundEffect("minigameFail", state.soundEnabled);
      addLogEntry(C().ui.outcomes.emailFail, "danger");
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 8);
    }

  } else if (eventType === SpecialEventType.KalenderTetris) {
    const challenge = getRandomKalenderTetris();
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.calendarTetris, challenge.context), "warning");
    addLogEntry(C().ui.minigames.findFreeSlot, "warning");
    const mods = getPerkModifiers(state);
    const correct = await waitForKalenderTetris(challenge.participants, challenge.timeSlots, challenge.correctSlot, panel, Math.round(challenge.timeMs * mods.minigameTimeMult));
    if (correct) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.kalenderWin, "success");
      state.player.fatigue = Math.max(0, state.player.fatigue - 10);
    } else {
      state.dayStats.minigamesLost++;
      addLogEntry(C().ui.outcomes.kalenderFail, "danger");
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 5);
    }

  } else if (eventType === SpecialEventType.CodeReview) {
    const challenge = getRandomCodeReview();
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.codeReview, challenge.context), "warning");
    addLogEntry(C().ui.minigames.codeLanguage(challenge.language), "warning");
    const mods = getPerkModifiers(state);
    const correct = await waitForCodeReview(challenge.code, challenge.options, challenge.correctIndex, challenge.language, panel, Math.round(challenge.timeMs * mods.minigameTimeMult));
    if (correct) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.codeReviewWin, "success");
      state.player.fatigue = Math.max(0, state.player.fatigue - 12);
    } else {
      state.dayStats.minigamesLost++;
      playSoundEffect("minigameFail", state.soundEnabled);
      addLogEntry(C().ui.outcomes.codeReviewFail, "danger");
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 5);
    }

  } else if (eventType === SpecialEventType.KiHalluzination) {
    const challenge = getRandomKiHalluzination();
    const total = challenge.hallucinations.length;
    addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.aiHallucination, challenge.context), "warning");
    addLogEntry(C().ui.minigames.assistantAnnounce(challenge.tool), "warning");
    const mods = getPerkModifiers(state);
    const hits = await waitForKiHalluzination(
      challenge.statements, challenge.hallucinations, challenge.tool, challenge.task,
      panel, Math.round(challenge.timeMs * mods.minigameTimeMult),
    );
    if (hits === total) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.kiPerfect(hits, total), "success");
      state.player.fatigue = Math.max(0, state.player.fatigue - 12);
    } else if (hits > 0) {
      state.dayStats.minigamesWon++;
      state.dayStats.bingoMinigameWon = true;
      playSoundEffect("minigameWin", state.soundEnabled);
      addLogEntry(C().ui.outcomes.kiPartial(hits, total), "warning");
      state.player.fatigue = Math.max(0, state.player.fatigue - 5);
    } else {
      state.dayStats.minigamesLost++;
      playSoundEffect("minigameFail", state.soundEnabled);
      addLogEntry(C().ui.outcomes.kiFail, "danger");
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 6);
    }
  }
}

// ---- Powerup Application ----

function applyPowerup(state: GameState, id: PowerupId, meeting?: import("./types").Meeting): void {
  const def = powerupDefs().find((p) => p.id === id)!;
  /** The locale writes the line; only the powerup's own name is spliced in. */
  const announce = () =>
    addLogEntry(C().ui.powerups.activation[id].replace("{name}", def.name), "success");
  switch (id) {
    case PowerupId.Kaffee:
      state.player.fatigue = Math.max(0, state.player.fatigue - 25);
      state.sessionStats.totalKaffeeUsed++;
      announce();
      break;
    case PowerupId.NoiseCancelling:
      state.activePowerups.push({ id, remainingTurns: -1 });
      announce();
      break;
    case PowerupId.MuteButton:
      state.activePowerups.push({ id, remainingTurns: -1 });
      announce();
      break;
    case PowerupId.PizzaBestellen:
      state.activePowerups.push({ id, remainingTurns: -1 });
      announce();
      break;
    case PowerupId.HandyAkku:
      state.activePowerups.push({ id, remainingTurns: 1 });
      announce();
      break;
    case PowerupId.FakeNotizen:
      state.activePowerups.push({ id, remainingTurns: 1 });
      announce();
      break;
    case PowerupId.LaptopTarnung:
      state.activePowerups.push({ id, remainingTurns: 3 });
      announce();
      break;
    case PowerupId.RemoteJoker:
      if (meeting) meeting.isOnline = true;
      announce();
      break;
    case PowerupId.BullshitBingo:
      state.activePowerups.push({ id, remainingTurns: -1 });
      announce();
      break;
    // Prompt library: meeting-scoped, so it is cleared with the other -1s at
    // the end of the meeting. The timer bonus itself lives in getPerkModifiers.
    case PowerupId.PromptBibliothek:
      state.activePowerups.push({ id, remainingTurns: -1 });
      announce();
      break;
    // The AI already summarised it, so there is less of it left to sit through.
    case PowerupId.KiZusammenfassung:
      if (meeting) {
        // Both, so the round counter on screen tells the same story as the loop.
        const shorter = Math.max(1, meeting.roundsRemaining - 2);
        meeting.roundsTotal -= meeting.roundsRemaining - shorter;
        meeting.roundsRemaining = shorter;
      }
      announce();
      break;
    // The agent worked the night shift: it billed hours, you read its output.
    case PowerupId.KiAgent:
      state.player.salary += 150;
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 10);
      updatePeakSalary(state);
      announce();
      break;
  }
}

// ---- Boss Meeting Loop (Vorstandsmeeting) ----
// No procrastinating, no powerup skipping. Just tasks fired at you.
// Wrong answer = salary penalty. Survive all rounds = salary bonus.

async function runBossMeeting(state: GameState): Promise<{ fellAsleep: boolean }> {
  const meeting = state.currentMeeting!;
  const panel = getActionPanel();
  let mistakes = 0;
  const totalTasks = meeting.roundsTotal;
  const BOSS_WRONG_PENALTY = 75;
  const BOSS_SUCCESS_BONUS = 150;

  // Reset per-meeting achievement tracking
  state.achievementTracker.wasAddressedThisMeeting = true; // always addressed in boss meetings
  state.achievementTracker.allCorrectThisMeeting = true;
  state.achievementTracker.questionsThisMeeting = 0;

  showMeetingHeader(meeting);
  renderParticipants(meeting, -1);
  clearLog();

  addLogEntry(C().ui.boss.intro(totalTasks), "danger");
  addLogEntry(C().ui.boss.stakes(BOSS_WRONG_PENALTY, BOSS_SUCCESS_BONUS), "warning");
  if (Math.random() < 0.8) {
    addLogEntry(`💭 <em>${getRandomFlavor()}</em>`, "flavor");
  }
  await delay(DELAY_MEETING_INTRO);

  for (let task = 0; task < totalTasks && !state.isGameOver; task++) {
    addLogEntry(C().ui.boss.taskHeader(task + 1, totalTasks), "flavor");
    updateStatusBar(state.player, state.meta.impostorPoints);

    // Pick a random task type: question (35%), brainstorming (15%), simon says (12%),
    // buzzword bingo (10%), email sort (8%), kalender tetris (8%), code review (6%),
    // KI-Halluzination (6%)
    const roll = Math.random();
    const mods = getPerkModifiers(state);

    if (roll < 0.35) {
      // Boss Question
      const q = getRandomBossQuestion();
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.boss.question(q.prompt), "warning");
      const choices = q.answers.map((a, i) => ({
        label: `${i + 1}. ${a}`, value: String(i), className: "answer",
      }));
      const answer = await waitForChoice(choices, panel, Math.round(TURN_TIMEOUT_MS * mods.minigameTimeMult), `❓ ${q.prompt}`);
      clearActionPanel();
      let correct = false;
      if (answer !== null) {
        correct = parseInt(answer) === q.correctIndex;
        addLogEntry(C().ui.actions.yourAnswer(q.answers[parseInt(answer)]), "normal");
      } else {
        addLogEntry(C().ui.actions.noAnswer, "warning");
      }
      state.achievementTracker.questionsThisMeeting++;
      if (correct) {
        playSoundEffect("minigameWin", state.soundEnabled);
        state.dayStats.bingoQuestionCorrect = true;
        addLogEntry(C().ui.boss.questionWin, "success");
      } else {
        mistakes++;
        state.achievementTracker.allCorrectThisMeeting = false;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.questionFail(BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }

    } else if (roll < 0.50) {
      // Brainstorming
      const challenge = getRandomBrainstorming();
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.brainstorming, challenge.context), "warning");
      addLogEntry(C().ui.minigames.typeLetterByLetter(challenge.word), "warning");
      const progressBar = createProgressBar(panel, challenge.word.length);
      const success = await waitForCharByChar(challenge.word, panel, Math.round(challenge.timeMs * mods.minigameTimeMult), progressBar.update);
      progressBar.remove();
      if (success) {
        state.dayStats.minigamesWon++;
        state.dayStats.bingoMinigameWon = true;
        playSoundEffect("minigameWin", state.soundEnabled);
        addLogEntry(C().ui.boss.brainstormWin, "success");
      } else {
        mistakes++;
        state.dayStats.minigamesLost++;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.brainstormFail(BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }

    } else if (roll < 0.62) {
      // Simon Says
      const challenge = getRandomSimonSays();
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.teamBuilding, challenge.context), "warning");
      addLogEntry(C().ui.minigames.repeatSequence, "warning");
      const progressBar = createProgressBar(panel, challenge.sequence.length);
      const correct = await waitForSequence(challenge.sequence, panel, Math.round(challenge.timeMs * mods.minigameTimeMult), progressBar.update);
      progressBar.remove();
      if (correct) {
        state.dayStats.minigamesWon++;
        state.dayStats.bingoMinigameWon = true;
        playSoundEffect("minigameWin", state.soundEnabled);
        addLogEntry(C().ui.boss.simonWin, "success");
      } else {
        mistakes++;
        state.dayStats.minigamesLost++;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.simonFail(BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }

    } else if (roll < 0.72) {
      // Buzzword Bingo
      const challenge = getRandomBuzzwordBingo();
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.buzzwordBingo, challenge.context), "warning");
      addLogEntry(C().ui.minigames.memoriseOrderShort, "warning");
      const progressBar = createProgressBar(panel, challenge.words.length);
      const success = await waitForBuzzwordBingo(challenge.words, challenge.correctOrder, panel, Math.round(challenge.timeMs * mods.minigameTimeMult), progressBar.update);
      progressBar.remove();
      if (success) {
        state.dayStats.minigamesWon++;
        state.dayStats.bingoMinigameWon = true;
        playSoundEffect("minigameWin", state.soundEnabled);
        addLogEntry(C().ui.boss.bingoWin, "success");
      } else {
        mistakes++;
        state.dayStats.minigamesLost++;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.bingoFail(BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }

    } else if (roll < 0.80) {
      // Email Sort
      const challenge = getRandomEmailSortieren();
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.emailSorting, challenge.context), "warning");
      addLogEntry(C().ui.minigames.sortEmails, "warning");
      const correctCount = await waitForEmailSort(challenge.emails, panel, Math.round(challenge.timeMs * mods.minigameTimeMult));
      const total = challenge.emails.length;
      if (correctCount === total) {
        state.dayStats.minigamesWon++;
        state.dayStats.bingoMinigameWon = true;
        playSoundEffect("minigameWin", state.soundEnabled);
        addLogEntry(C().ui.boss.emailWin(total), "success");
      } else {
        mistakes++;
        state.dayStats.minigamesLost++;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.emailFail(correctCount, total, BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }

    } else if (roll < 0.88) {
      // Kalender Tetris
      const challenge = getRandomKalenderTetris();
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.calendarTetris, challenge.context), "warning");
      addLogEntry(C().ui.minigames.findFreeSlot, "warning");
      const correct = await waitForKalenderTetris(challenge.participants, challenge.timeSlots, challenge.correctSlot, panel, Math.round(challenge.timeMs * mods.minigameTimeMult));
      if (correct) {
        state.dayStats.minigamesWon++;
        state.dayStats.bingoMinigameWon = true;
        playSoundEffect("minigameWin", state.soundEnabled);
        addLogEntry(C().ui.boss.kalenderWin, "success");
      } else {
        mistakes++;
        state.dayStats.minigamesLost++;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.kalenderFail(BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }

    } else if (roll < 0.94) {
      // Code Review
      const challenge = getRandomCodeReview();
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.codeReview, challenge.context), "warning");
      addLogEntry(C().ui.minigames.codeLanguage(challenge.language), "warning");
      const correct = await waitForCodeReview(challenge.code, challenge.options, challenge.correctIndex, challenge.language, panel, Math.round(challenge.timeMs * mods.minigameTimeMult));
      if (correct) {
        state.dayStats.minigamesWon++;
        state.dayStats.bingoMinigameWon = true;
        playSoundEffect("minigameWin", state.soundEnabled);
        addLogEntry(C().ui.boss.codeReviewWin, "success");
      } else {
        mistakes++;
        state.dayStats.minigamesLost++;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.codeReviewFail(BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }

    } else {
      // KI-Halluzination
      const challenge = getRandomKiHalluzination();
      const total = challenge.hallucinations.length;
      playSoundEffect("addressed", state.soundEnabled);
      addLogEntry(C().ui.minigames.announceLine(C().ui.minigames.announce.aiHallucination, challenge.context), "warning");
      addLogEntry(C().ui.minigames.assistantAnnounce(challenge.tool), "warning");
      const hits = await waitForKiHalluzination(
        challenge.statements, challenge.hallucinations, challenge.tool, challenge.task,
        panel, Math.round(challenge.timeMs * mods.minigameTimeMult),
      );
      if (hits === total) {
        state.dayStats.minigamesWon++;
        state.dayStats.bingoMinigameWon = true;
        playSoundEffect("minigameWin", state.soundEnabled);
        addLogEntry(C().ui.boss.kiWin, "success");
      } else {
        mistakes++;
        state.dayStats.minigamesLost++;
        playSoundEffect("salaryPenalty", state.soundEnabled);
        state.player.salary -= BOSS_WRONG_PENALTY;
        addLogEntry(C().ui.boss.kiFail(hits, total, BOSS_WRONG_PENALTY), "danger");
        screenShake("medium");
      }
    }

    // Mid-task flavor
    if (task < totalTasks - 1 && Math.random() < 0.3) {
      addLogEntry(`💭 <em>${getRandomFlavor()}</em>`, "flavor");
    }

    updateStatusBar(state.player, state.meta.impostorPoints);

    // Check game over after salary penalty
    if (checkGameOver(state)) return { fellAsleep: false };

    await delay(1200);
  }

  // Survived all tasks
  if (!state.isGameOver) {
    if (mistakes === 0) {
      playSoundEffect("salaryBonus", state.soundEnabled);
      state.player.salary += BOSS_SUCCESS_BONUS;
      addLogEntry(C().ui.boss.flawless(BOSS_SUCCESS_BONUS), "success");
      screenShake("light");
    } else if (mistakes <= Math.floor(totalTasks / 2)) {
      const halfBonus = Math.round(BOSS_SUCCESS_BONUS / 2);
      playSoundEffect("salaryBonus", state.soundEnabled);
      state.player.salary += halfBonus;
      addLogEntry(C().ui.boss.withMistakes(mistakes, halfBonus), "success");
    } else {
      addLogEntry(C().ui.boss.noBonus(mistakes), "warning");
    }
    state.sessionStats.totalBossMeetings = (state.sessionStats.totalBossMeetings || 0) + 1;
    updateStatusBar(state.player, state.meta.impostorPoints);
  }

  // Update session stats at end
  updateSessionStatsAfterMeeting(state);

  // Bingo tracking for boss meetings
  state.dayStats.bingoMeetingSurvived = true;
  checkBingoGoals(state);
  renderBingoCard(state.bingoCard);
  if (isBingoComplete(state)) {
    playSoundEffect("bingoComplete", state.soundEnabled);
    addLogEntry(C().ui.bingo.complete, "success");
    state.player.fatigue = Math.max(0, state.player.fatigue - 20);
    state.player.salary += 100;
    state.bingoCard = null;
    renderBingoCard(null);
  }

  return { fellAsleep: false };
}

// ---- Meeting Loop ----

async function runMeeting(state: GameState): Promise<{ fellAsleep: boolean }> {
  const meeting = state.currentMeeting!;
  const panel = getActionPanel();
  let fellAsleep = false;

  // Reset per-meeting achievement tracking
  state.achievementTracker.wasAddressedThisMeeting = false;
  state.achievementTracker.spokeUnpromptedThisMeeting = false;
  state.achievementTracker.allCorrectThisMeeting = true;
  state.achievementTracker.questionsThisMeeting = 0;

  showMeetingHeader(meeting);
  renderParticipants(meeting, -1);
  clearLog();

  // Bullshit-Bingo: count buzzwords from colleagues
  const hasBullshitBingo = state.activePowerups.some((p) => p.id === PowerupId.BullshitBingo);
  let buzzwordCount = 0;

  // Flavor text at start (narrator, not attributed to a speaker)
  if (Math.random() < 0.7) {
    addLogEntry(`💭 <em>${getRandomFlavor()}</em>`, "flavor");
  }

  await delay(DELAY_MEETING_INTRO);

  // Track whether the player was addressed (persists across direction changes)
  let playerAddressedThisRound = false;
  let addressMethod: AddressMethod = "informal";
  let addressByIdx = 0;

  while (meeting.roundsRemaining > 0 && !state.isGameOver) {
    // Process each speaker in the turn order (one full round)
    meeting.currentSpeakerIdx = meeting.directionForward ? 0 : meeting.turnOrder.length - 1;

    for (let step = 0; step < meeting.turnOrder.length && !state.isGameOver; step++) {
      const currentSlot = getCurrentSlot(meeting);
      const playerTurn = currentSlot === meeting.playerSlot;

      renderParticipants(meeting, currentSlot);
      updateMeetingMeta(meeting);

      // Direction change check
      const dirChanged = checkDirectionChange(meeting);
      if (dirChanged) {
        playSoundEffect("directionChange", state.soundEnabled);
        screenShake("light");
        // Pick a random non-player colleague to attribute the direction change to
        const nonPlayerSlots = meeting.turnOrder.filter((s) => s !== meeting.playerSlot);
        const dirColIdx = pick(nonPlayerSlots);
        const dirCol = meeting.participants[dirColIdx];
        const dirNeon = getNeonClass(dirCol.role);
        const dirQuote = getDirectionChangeQuote();
        addLogEntry(`🔀 <span class="speaker-name ${dirNeon}">${dirCol.name}</span>: <span class="${dirNeon}">"${dirQuote}"</span> ${C().ui.meeting.directionReversed}`, "warning");
        await delay(800);
        state.dayStats.bingoDirectionChange = true;
      }

      if (playerTurn) {
        // ---- PLAYER'S TURN ----
        // Tick active powerups
        tickPowerups(state);

        playSoundEffect("playerTurn", state.soundEnabled);
        addLogEntry("🧑 <strong>Du bist dran.</strong>", "normal");

        // Check for usable mid-meeting powerups
        const hasMeetingPowerups = powerupDefs().some(
          (pw) => pw.usableInMeeting && state.meta.powerupInventory[pw.id] > 0,
        );

        const choices: { label: string; value: string; className?: string }[] = [
          { label: C().ui.actions.procrastinate, value: "p", className: "procrastinate" },
          { label: C().ui.actions.action, value: "a", className: "action" },
        ];
        if (hasMeetingPowerups) {
          choices.push({ label: C().ui.actions.powerup, value: "pw", className: "powerup" });
        }
        const input = await waitForChoice(choices, panel, TURN_TIMEOUT_MS);
        clearActionPanel();

        // Handle powerup selection first, then re-prompt
        if (input === "pw") {
          const selectedPw = await showPowerupSelect(state.meta, "meeting");
          if (selectedPw !== null) {
            state.meta.powerupInventory[selectedPw]--;
            saveMeta(state.meta);
            playSoundEffect("powerUp", state.soundEnabled);
            applyPowerup(state, selectedPw, meeting);
          }
          // After using a powerup, still let the player act — treat as timeout to keep pace
          addLogEntry(C().ui.actions.powerupUsedTurnOver, "normal");
        } else {
          const action =
            input === null ? ActionType.Timeout
              : input === "p" ? ActionType.Procrastinate
              : ActionType.Action;

        if (action === ActionType.Timeout) {
          if (playerAddressedThisRound) {
            // Addressed but didn't respond = caught like procrastinating
            addLogEntry("⏰ Du hast nicht reagiert — du wurdest angesprochen!", "danger");
            const result = processProcrastinate(state, true);
            state.sessionStats.totalProcrastinations++;
            state.dayStats.bingoProcrastinateCount++;
            state.dayStats.timesCaught++;
            state.dayStats.bingoNoCatch = false;
            state.achievementTracker.consecutiveSuccessfulProcs = 0;
            screenShake("medium");
            playSoundEffect("caught", state.soundEnabled);
            await showCaughtAnimation();
            addLogEntry(result.message, "danger");
          } else {
            // Not addressed, did nothing — no benefit, no penalty
            playSoundEffect("timeout", state.soundEnabled);
            addLogEntry(C().ui.actions.staredIntoSpace, "warning");
          }
        } else if (action === ActionType.Procrastinate) {
          const result = processProcrastinate(state, playerAddressedThisRound);
          state.sessionStats.totalProcrastinations++;
          state.dayStats.bingoProcrastinateCount++;
          if (result.caughtProcrastinating) {
            state.dayStats.timesCaught++;
            state.dayStats.bingoNoCatch = false;
            state.achievementTracker.consecutiveSuccessfulProcs = 0;
            screenShake("medium");
            playSoundEffect("caught", state.soundEnabled);
            await showCaughtAnimation();
          } else {
            playSoundEffect("procrastinateSuccess", state.soundEnabled);
            state.sessionStats.totalSuccessfulProcrastinations++;
            state.achievementTracker.consecutiveSuccessfulProcs++;
          }
          addLogEntry(result.message, result.caughtProcrastinating ? "danger" : "success");
        } else {
          // Action
          if (playerAddressedThisRound) {
            // FakeNotizen: auto-answer correctly
            const fakeIdx = state.activePowerups.findIndex((p) => p.id === PowerupId.FakeNotizen);
            if (fakeIdx >= 0) {
              state.activePowerups.splice(fakeIdx, 1);
              addLogEntry(C().ui.turns.fakeNotes, "success");
              const result = processQuestionAnswer(state, true);
              state.achievementTracker.questionsThisMeeting++;
              state.dayStats.bingoQuestionCorrect = true;
              playSoundEffect("minigameWin", state.soundEnabled);
              addLogEntry(result.message, "success");
            } else {
              // Good — respond to the addressing
              const specialEvent = rollForSpecialEvent();
              if (specialEvent !== SpecialEventType.None) {
                await handleSpecialEvent(specialEvent, state, panel);
              } else {
                const q = getRandomQuestion();
                addLogEntry(`${q.prompt}`, "normal");
                const choices = q.answers.map((a, i) => ({
                  label: `${i + 1}. ${a}`, value: String(i), className: "answer",
                }));
                const answer = await waitForChoice(choices, panel, TURN_TIMEOUT_MS, `❓ ${q.prompt}`);
                clearActionPanel();
                let correct = false;
                if (answer !== null) {
                  correct = parseInt(answer) === q.correctIndex;
                  addLogEntry(C().ui.actions.yourAnswer(q.answers[parseInt(answer)]), "normal");
                } else {
                  addLogEntry(C().ui.actions.noAnswer, "warning");
                }
                const result = processQuestionAnswer(state, correct);
                state.achievementTracker.questionsThisMeeting++;
                if (correct) { state.dayStats.bingoQuestionCorrect = true; playSoundEffect("minigameWin", state.soundEnabled); }
                else { state.achievementTracker.allCorrectThisMeeting = false; playSoundEffect("minigameFail", state.soundEnabled); }
                addLogEntry(result.message, correct ? "success" : "danger");
              }
            }
          } else {
            // Not addressed but chose Action = monologue
            playSoundEffect("monologue", state.soundEnabled);
            const result = processUnpromptedAction(state);
            state.achievementTracker.monologuesToday++;
            state.achievementTracker.spokeUnpromptedThisMeeting = true;
            state.sessionStats.totalMonologues++;
            state.dayStats.monologues++;
            state.dayStats.bingoMonologue = true;
            addLogEntry(result.message, "warning");
          }
        }
        } // end of else (not powerup)
        playerAddressedThisRound = false;
      } else {
        // ---- COLLEAGUE'S TURN ----
        const colIdx = currentSlot;
        const result = applyColleagueTurn(state, colIdx);
        const col = meeting.participants[colIdx];
        const neonCls = getNeonClass(col.role);
        // Track colleague fatigue for stats
        trackColleagueFatigue(state, col.role, result.fatigueDelta);
        // Update relationship: more fatigue = slightly negative
        updateRelationship(state, col.name, col.role, result.fatigueDelta > 5 ? -0.3 : 0.1);
        const relTag = getRelationshipTag(state, col.name, col.role);
        // Bullshit-Bingo: each colleague speaking = 1 buzzword
        if (hasBullshitBingo) buzzwordCount++;

        // Check if this colleague addresses the player BEFORE logging speech
        // so we can combine them into one log entry instead of two
        const hasMute = state.activePowerups.some((p) => p.id === PowerupId.MuteButton);
        const addressCheck = checkIfPlayerAddressed(meeting, state.player.role, colIdx, state.meta, state.currentDay, state.relationships);
        const addrMods = getPerkModifiers(state);
        const addressEffective = addressCheck.addressed && (addrMods.addressChanceMult >= 1.0 || Math.random() < addrMods.addressChanceMult);
        const willAddress = addressEffective && !hasMute;

        if (willAddress) {
          // Colleague addresses the player — show only the address phrase (no separate speech)
          playerAddressedThisRound = true;
          addressMethod = addressCheck.method;
          addressByIdx = addressCheck.byColleagueIdx;
          state.achievementTracker.wasAddressedThisMeeting = true;
          updateRelationship(state, col.name, col.role, -0.5);
          playSoundEffect("addressed", state.soundEnabled);

          const phrase = generateAddressPhrase(state.player.name, state.player.role, addressMethod, col.name, col.role);
          addLogEntry(`${phrase} <span class="fatigue-delta">+${result.fatigueDelta} 😴</span>`, "normal");
        } else {
          // Normal colleague speech
          playSoundEffect("colleagueSpeak", state.soundEnabled);
          addLogEntry(
            `💬 <span class="speaker-name ${neonCls}">${col.name}</span> ${relTag}<span class="role-tag">${col.role}</span>: <span class="${neonCls}">"${result.message}"</span> <span class="fatigue-delta">+${result.fatigueDelta} 😴</span>`,
            "normal",
          );
        }

        // Mid-meeting flavor interjection (~15% chance per colleague turn)
        if (Math.random() < 0.15) {
          addLogEntry(`💭 <em>${getRandomFlavor()}</em>`, "flavor");
        }

        await delay(DELAY_COLLEAGUE_SPEAK);
      }

      // Check fell asleep
      const sleepResult = checkFellAsleep(state);
      if (sleepResult) {
        fellAsleep = true;
        state.dayStats.bingoNoSleep = false;
        screenShake("heavy");
        playSoundEffect("fellAsleep", state.soundEnabled);
        await showSleepingAnimation();
        addLogEntry(sleepResult.message, "danger");
        updateStatusBar(state.player, state.meta.impostorPoints);
        if (checkGameOver(state)) return { fellAsleep };
        await delay(DELAY_RESULT_SHOW);
      }

      if (checkGameOver(state)) return { fellAsleep };

      updateStatusBar(state.player, state.meta.impostorPoints);
      updateInfoPanel(state);

      // Advance to next speaker
      const roundDone = advanceToNextSpeaker(meeting);
      if (roundDone) break; // round complete, go to next round
    }

    // Between rounds
    if (meeting.roundsRemaining > 0 && !state.isGameOver) {
      addLogEntry(C().ui.meeting.roundEnded(meeting.roundsRemaining), "flavor");
      await delay(DELAY_BETWEEN_ROUNDS);
    }
  }

  // Bullshit-Bingo bonus at meeting end
  if (hasBullshitBingo && buzzwordCount > 0) {
    const bonus = buzzwordCount * 2;
    state.player.fatigue = Math.max(0, state.player.fatigue - bonus);
    addLogEntry(C().ui.bingo.buzzwordBonus(buzzwordCount, bonus), "success");
    if (buzzwordCount > state.achievementTracker.maxBuzzwordsInMeeting) {
      state.achievementTracker.maxBuzzwordsInMeeting = buzzwordCount;
    }
  }

  // Update session stats & check achievements at meeting end
  updateSessionStatsAfterMeeting(state);

  // Update bingo: mark meeting survived and invisible
  state.dayStats.bingoMeetingSurvived = true;
  if (!state.achievementTracker.wasAddressedThisMeeting) state.dayStats.bingoInvisible = true;
  checkBingoGoals(state);
  renderBingoCard(state.bingoCard);
  if (isBingoComplete(state)) {
    playSoundEffect("bingoComplete", state.soundEnabled);
    addLogEntry(C().ui.bingo.complete, "success");
    state.player.fatigue = Math.max(0, state.player.fatigue - 20);
    state.player.salary += 100;
    state.bingoCard = null; // consumed
    renderBingoCard(null);
  }

  // Track Flüsterer: if player didn't speak unprompted
  if (!state.achievementTracker.spokeUnpromptedThisMeeting) {
    state.achievementTracker.consecutiveSilentMeetings++;
  } else {
    state.achievementTracker.consecutiveSilentMeetings = 0;
  }
  const meetingAchievements = checkAndAwardAchievements(state);
  for (const id of meetingAchievements) {
    const def = achievementDefs().find((a) => a.id === id)!;
    addLogEntry(C().ui.achievements.unlocked(def.emoji, def.name, def.description), "success");
    playSoundEffect("achievement", state.soundEnabled);
  }

  // Clear meeting-specific powerups
  state.activePowerups = state.activePowerups.filter((p) => p.remainingTurns !== -1);

  return { fellAsleep };
}

// ---- Escape / Quit ----

let escapeRequested = false;

function showEscapeDialog(): Promise<boolean> {
  return new Promise((resolve) => {
    const esc = C().ui.escape;
    const overlay = document.createElement("div");
    overlay.className = "escape-overlay";
    overlay.innerHTML = `
      <div class="escape-dialog">
        <h3>${esc.heading}</h3>
        <p>${esc.body}</p>
        <div class="choice-row">
          <button class="choice-btn action" id="escape-confirm">${esc.confirm}</button>
          <button class="choice-btn procrastinate" id="escape-cancel">${esc.cancel}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const cleanup = () => { if (overlay.parentElement) overlay.remove(); };

    document.getElementById("escape-confirm")!.addEventListener("click", () => {
      cleanup();
      resolve(true);
    }, { once: true });

    document.getElementById("escape-cancel")!.addEventListener("click", () => {
      cleanup();
      resolve(false);
    }, { once: true });
  });
}

function initEscapeHandler(state: GameState): void {
  document.addEventListener("keydown", async (e) => {
    if (e.key !== "Escape") return;
    if (escapeRequested) return; // prevent double-trigger
    // Only active during game screen
    if (!document.getElementById("game-screen")?.classList.contains("active")) return;

    escapeRequested = true;
    const confirmed = await showEscapeDialog();
    if (confirmed) {
      // Save meta to server, then reload
      saveMeta(state.meta);
      await saveGameProgress(state);
      location.reload();
    } else {
      escapeRequested = false;
    }
  });
}

// ---- Theme Toggle ----

function initThemeToggle(): void {
  const btn = document.getElementById("theme-toggle");
  if (!btn) return;

  // Restore saved preference
  if (localStorage.getItem("manic-meeter-retro") === "1") {
    document.body.classList.add("retro");
    btn.textContent = "◻️ MODERN";
  }

  btn.addEventListener("click", () => {
    document.body.classList.toggle("retro");
    const isRetro = document.body.classList.contains("retro");
    btn.textContent = isRetro ? C().ui.controls.themeLabelModern : C().ui.controls.themeLabel;
    localStorage.setItem("manic-meeter-retro", isRetro ? "1" : "0");
  });
}

// ---- Main ----

// Global sound state (before game state exists)
let globalSoundEnabled = localStorage.getItem("manic-meeter-sound") !== "0";

/**
 * The language switch. Everything the game paints from state is repainted
 * immediately; log entries already written stay in the language they were
 * written in, because rewriting somebody's history mid-run is worse than a
 * bilingual scrollback.
 */
function initLanguageToggle(state: () => GameState | null): void {
  const btn = document.getElementById("lang-toggle");
  if (!btn) return;
  btn.addEventListener("click", () => setLocale(otherLocale()));

  onLocaleChange(() => {
    applyStaticText();
    populateRoleSelect();
    const s = state();
    if (!s) return;
    updateStatusBar(s.player, s.meta.impostorPoints);
    updateInfoPanel(s);
    renderBingoCard(s.bingoCard);
    if (s.currentMeeting) {
      showMeetingHeader(s.currentMeeting);
      renderParticipants(s.currentMeeting, -1);
    }
  });
}

/** Fill the difficulty picker with localised role names, keeping the selection. */
function populateRoleSelect(): void {
  const roleSelect = document.getElementById("player-role") as HTMLSelectElement | null;
  if (!roleSelect) return;
  const previous = roleSelect.value || localStorage.getItem("manic_lastRole");
  roleSelect.innerHTML = "";
  for (const r of Object.values(PlayerRole)) {
    const opt = document.createElement("option");
    opt.value = r;
    const stars = PLAYER_ROLE_STARS[r];
    opt.textContent = `${playerRoleLabel(r)} ${"★".repeat(stars)}${"☆".repeat(4 - stars)}`;
    if (previous === r) opt.selected = true;
    roleSelect.appendChild(opt);
  }
}

/** Set once the run starts, so the language switch can repaint a live game. */
let liveState: GameState | null = null;

async function main(): Promise<void> {
  applyStaticText();
  initRenderer();
  initThemeToggle();
  initLanguageToggle(() => liveState);
  initCookieBanner();

  // Auth: check existing session
  currentAuth = await initAuth();
  updateAuthStatus();
  setupAuthButtons();

  // Ensure a guest session exists for API calls
  await ensureGuestSession();
  if (!currentAuth.loggedIn) {
    currentAuth = await initAuth();
    updateAuthStatus();
  }

  // Preload achievements for start screen display
  if (isLoggedIn()) {
    try {
      const earlyProfile = await getProfile();
      if (earlyProfile.ok && earlyProfile.data && Array.isArray(earlyProfile.data.achievements)) {
        const ids = earlyProfile.data.achievements.filter(
          (a: string) => Object.values(AchievementId).includes(a as AchievementId)
        ) as AchievementId[];
        startScreenAchievements = new Set(ids);
      }
    } catch {
      // Silent
    }
  }

  const { name, role } = await setupCharacterCreation();

  // Set active role for role-keyed persistence
  setActiveRole(role);

  // Load meta-progression
  const meta = await loadMeta();

  // Load saved achievements and stats from server
  let loadedAchievements: AchievementId[] = [];
  let loadedStats: SessionStats | undefined;
  if (isLoggedIn()) {
    try {
      const profileRes = await getProfile();
      if (profileRes.ok && profileRes.data) {
        if (Array.isArray(profileRes.data.achievements)) {
          loadedAchievements = profileRes.data.achievements.filter(
            (a: string) => Object.values(AchievementId).includes(a as AchievementId)
          ) as AchievementId[];
        }
        if (profileRes.data.stats && typeof profileRes.data.stats === "object") {
          loadedStats = profileRes.data.stats as unknown as SessionStats;
        }
      }
    } catch {
      // Silent fail
    }
  }

  // Show shop BEFORE creating player so stat upgrades (Gehalt, MaxMüdigkeit) take immediate effect
  showScreen("game-screen");

  if (meta.impostorPoints > 0 || meta.totalPointsEarned > 0) {
    await showShop(meta);
  }

  const player = createPlayer(name, role, meta);
  const state = createGameState(player, meta, loadedAchievements, loadedStats);
  liveState = state;
  state.soundEnabled = globalSoundEnabled;
  // Keep sound state in sync with toggle
  initSoundToggle((enabled) => { globalSoundEnabled = enabled; state.soundEnabled = enabled; });
  startScreenAchievements = state.achievements;

  initEscapeHandler(state);
  updateStatusBar(player, meta.impostorPoints);
  updateInfoPanel(state);
  clearLog();

  addLogEntry(C().ui.day.welcome(name, playerRoleLabel(role)), "normal");
  addLogEntry(C().ui.day.startingSalary(player.salary, MIN_WAGE), "normal");
  addLogEntry(C().ui.day.goal, "normal");

  const panel = getActionPanel();
  await waitForContinue(panel, C().ui.start.btnStart);

  while (!state.isGameOver) {
    // Show day schedule
    const fullSchedule = state.meetingQueue.map((m) => meetingTypeLabel(m));
    let meetingIndex = 0;
    showNewDay(state.currentDay, fullSchedule);
    updateStatusBar(state.player, state.meta.impostorPoints);
    updateInfoPanel(state);
    clearLog();
    playSoundEffect("dayStart", state.soundEnabled);
    addLogEntry(C().ui.day.dayHeader(state.currentDay, state.meetingQueue.length), "normal");

    // Show active perks
    if (state.activePerks.length > 0) {
      const perkNames = state.activePerks.map((id) => {
        const def = dailyPerks().find((p) => p.id === id);
        return def ? `${def.emoji} ${def.name}` : id;
      });
      addLogEntry(C().ui.day.activePerks(perkNames.join(", ")), "flavor");
    }
    if (state.attestDaysRemaining > 0) {
      addLogEntry(C().ui.day.attestActive(state.attestDaysRemaining), "success");
    }

    // Daily perk roll (random bonus or malus at day start)
    const dailyPerk = rollDailyPerk(state);
    if (dailyPerk) {
      playSoundEffect(dailyPerk.isPositive ? "perkPositive" : "perkNegative", state.soundEnabled);
      addLogEntry(C().ui.day.perkLine(dailyPerk.emoji, dailyPerk.isPositive ? C().ui.day.perkBonus : C().ui.day.perkMalus, dailyPerk.name, dailyPerk.description), dailyPerk.isPositive ? "success" : "danger");
      state.activePerks.push(dailyPerk.id);
      updateInfoPanel(state);
    }

    // Apply "lange gezockt" perk: extra fatigue at day start
    if (state.activePerks.includes("langeGezockt")) {
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 20);
      addLogEntry(C().ui.day.perkLateGaming, "danger");
    }
    // Apply "stau" perk: extra fatigue at day start
    if (state.activePerks.includes("stau")) {
      state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 15);
      addLogEntry(C().ui.day.perkTraffic, "danger");
    }
    // Apply "parkplatz gefunden" perk: salary bonus applied via salaryPerDay in getPerkModifiers

    // Attest drop: extremely rare (< 0.5%)
    if (state.attestDaysRemaining <= 0 && rollAttest()) {
      state.attestDaysRemaining = 5;
      addLogEntry(C().ui.day.attestFound, "success");
      addLogEntry(C().ui.day.attestEffects, "success");
    }

    // Generate bingo card for the day
    state.bingoCard = generateBingoCard();
    renderBingoCard(state.bingoCard);

    // Weekend event every 7th day
    if (state.currentDay > 1 && state.currentDay % 7 === 0 && weekendEvents().length > 0) {
      const evt = pick(weekendEvents());
      state.isWeekendEvent = true;
      state.weekendEvent = evt;
      showWeekendEvent(evt.emoji, weekendEventLabel(evt.type), evt.description);
      if (evt.bonusSalary) {
        state.player.salary += evt.bonusSalary;
        addLogEntry(C().ui.day.weekendBonusSalary(evt.bonusSalary, weekendEventLabel(evt.type)), "success");
      }
      if (evt.bonusFatigueReduction) {
        state.player.fatigue = Math.max(0, state.player.fatigue - evt.bonusFatigueReduction);
        addLogEntry(C().ui.day.weekendBonusFatigue(evt.bonusFatigueReduction, weekendEventLabel(evt.type)), "success");
      }
      // Force extra meetings from weekend event
      if (evt.meetingTypes && evt.meetingTypes.length > 0) {
        for (const mt of evt.meetingTypes) {
          state.meetingQueue.push(mt);
        }
        addLogEntry(C().ui.day.weekendExtraMeetings(evt.meetingTypes.length, weekendEventLabel(evt.type)), "warning");
      }
      updateStatusBar(state.player, state.meta.impostorPoints);
    } else {
      state.isWeekendEvent = false;
      state.weekendEvent = null;
    }

    // Homeoffice roll (permanent unlock). The queue holds meeting *types*, not
    // meetings — those are built one at a time by startNextMeeting — so the day
    // remembers the roll and each meeting is marked online as it starts.
    const homeofficeToday = rollHomeoffice(state.meta);
    if (homeofficeToday) {
      addLogEntry(C().ui.day.homeoffice, "success");
    }

    let fellAsleepThisDay = false;

    await waitForContinue(panel, C().ui.actions.startDay);

    // Process each meeting in the queue
    while (state.meetingQueue.length > 0 && !state.isGameOver) {
      // Random encounter between meetings (20% chance, guaranteed every 3 days)
      const encounterForced = state.daysSinceLastEncounter >= 3;
      const encounterChance = encounterForced ? 1.0 : 0.20;
      if (Math.random() < encounterChance) {
        // 70% chance for random encounter, 30% for old positive event
        if (Math.random() < 0.7) {
          playSoundEffect("encounter", state.soundEnabled);
          await runEncounter(state, panel);
          await waitForContinue(panel, C().ui.actions.continue);
        } else {
          playSoundEffect("positiveEvent", state.soundEnabled);
          const posEvent = pick(positiveEvents());
          clearLog();
          addLogEntry(`🎉 <strong>${posEvent.title}</strong>`, "success");
          addLogEntry(posEvent.description, "flavor");
          if (posEvent.fatigueBonus) {
            state.player.fatigue = Math.max(0, state.player.fatigue - posEvent.fatigueBonus);
            addLogEntry(C().ui.encounter.fatigueDelta("-", posEvent.fatigueBonus!), "success");
          }
          if (posEvent.salaryBonus) {
            state.player.salary += posEvent.salaryBonus;
            addLogEntry(C().ui.encounter.salaryDelta("+", posEvent.salaryBonus!), "success");
          }
          if (posEvent.skipMeeting) {
            const skipped = state.meetingQueue.shift();
            if (skipped) {
              state.dayStats.meetingsSkipped++;
              addLogEntry(C().ui.day.meetingCancelled(meetingTypeLabel(skipped)), "success");
              meetingIndex++;
            }
          }
          updateStatusBar(state.player, state.meta.impostorPoints);
          updateInfoPanel(state);
          await waitForContinue(panel, C().ui.actions.continue);
        }
        if (state.meetingQueue.length === 0) break;
      }

      showMeetingProgress(fullSchedule, meetingIndex);
      const meeting = startNextMeeting(state);
      if (!meeting) break;
      if (homeofficeToday) meeting.isOnline = true;

      const isBoss = meeting.type === MeetingType.Vorstandsmeeting;

      // VPN-Zugang: chance to skip meeting (NOT boss meetings)
      if (!isBoss) {
        const vpnChance = getStatEffect(state.meta, StatUpgradeId.VPN);
        if (vpnChance > 0 && Math.random() < vpnChance) {
          clearLog();
          addLogEntry(C().ui.prep.vpnSkip, "success");
          addLogEntry(C().ui.prep.vpnSkipped(meetingTypeLabel(meeting.type)), "flavor");
          state.dayStats.meetingsSkipped++;
          completeMeeting(state);
          meetingIndex++;
          await delay(1500);
          continue;
        }
      }

      if (isBoss) {
        // ---- BOSS MEETING: no prep phase, no powerups, no skipping ----
        clearLog();
        addLogEntry(C().ui.boss.announce, "danger");
        addLogEntry(`${C().ui.meeting.participants(meeting.participants.length)} — ${C().ui.meeting.tasks(meeting.roundsTotal)}`, "normal");
        updateStatusBar(state.player, state.meta.impostorPoints);
        await waitForContinue(panel, C().ui.boss.enter);

        playSoundEffect("meetingStart", state.soundEnabled);
        const meetingResult = await runBossMeeting(state);

        // Boss meetings don't cause falling asleep
        if (!state.isGameOver) {
          completeMeeting(state);
          state.dayStats.meetingsSurvived++;
          state.dayStats.meetingTypesThisDay.push(meeting.type);
          state.sessionStats.totalMeetingsSurvived++;
          meetingIndex++;
          playSoundEffect("meetingEnd", state.soundEnabled);
          addLogEntry(C().ui.boss.survived, "success");
          await delay(800);
          if (state.meetingQueue.length > 0) {
            await waitForContinue(panel, C().ui.meeting.continueToNext);
          }
        }
      } else {
        // ---- VENDOR ENCOUNTER (before boss meetings — now disabled since boss has own flow) ----

        // ---- PREP PHASE (Rüstzeit) ----
        clearLog();
        showPrepPhase(meeting);
        addLogEntry(C().ui.meeting.next(meetingTypeLabel(meeting.type)), "normal");
        addLogEntry(`${C().ui.meeting.participants(meeting.participants.length)} — ${C().ui.meeting.rounds(meeting.roundsTotal)}`, "normal");

        // Card draw: 3 cards, one has a random prep-phase powerup, rest are empty.
        const prepPowerups = powerupDefs().filter((p) => p.usableInPrep);
        const cardPowerup = pick(prepPowerups);
        const winPosition = Math.floor(Math.random() * 3);
        const cardChoices = [
          { label: C().ui.prep.card(1), value: "0", className: "answer" },
          { label: C().ui.prep.card(2), value: "1", className: "answer" },
          { label: C().ui.prep.card(3), value: "2", className: "answer" },
        ];
        addLogEntry(C().ui.prep.heading, "warning");
        const picked = await waitForChoice(cardChoices, panel, 10000);
        clearActionPanel();

        playSoundEffect("cardDraw", state.soundEnabled);
        if (picked !== null && parseInt(picked) === winPosition) {
          playSoundEffect("powerUp", state.soundEnabled);
          addLogEntry(C().ui.prep.found(cardPowerup.name, cardPowerup.description), "success");
          applyPowerup(state, cardPowerup.id, meeting);
          state.sessionStats.totalCardDrawWins++;
        } else {
          addLogEntry(C().ui.prep.emptyCard, "flavor");
        }
        await delay(1000);

        // Prep-phase powerup selection
        const prepPowerup = await showPowerupSelect(state.meta, "prep");
        if (prepPowerup !== null) {
          state.meta.powerupInventory[prepPowerup]--;
          saveMeta(state.meta);
          applyPowerup(state, prepPowerup, meeting);
        }

        // Pizza skip
        if (state.activePowerups.some((p) => p.id === PowerupId.PizzaBestellen)) {
          state.activePowerups = state.activePowerups.filter((p) => p.id !== PowerupId.PizzaBestellen);
          addLogEntry(C().ui.prep.pizzaSkip, "success");
          state.dayStats.meetingsSkipped++;
          completeMeeting(state);
          meetingIndex++;
          await delay(1200);
          continue;
        }

        updateStatusBar(state.player, state.meta.impostorPoints);
        await waitForContinue(panel, meeting.isOnline ? C().ui.meeting.enterOnline : C().ui.meeting.enter);

        playSoundEffect("meetingStart", state.soundEnabled);
        const meetingResult = await runMeeting(state);
        if (meetingResult.fellAsleep) {
          fellAsleepThisDay = true;
          state.dayStats.timesFellAsleep++;
          state.sessionStats.totalTimesFellAsleep++;
          // Track meeting type failure
          const tKey = meeting.type;
          if (!state.sessionStats.meetingTypeStats) state.sessionStats.meetingTypeStats = {};
          if (!state.sessionStats.meetingTypeStats[tKey]) {
            state.sessionStats.meetingTypeStats[tKey] = { survived: 0, fellAsleep: 0, caught: 0 };
          }
          state.sessionStats.meetingTypeStats[tKey].fellAsleep++;
        }

        if (!state.isGameOver) {
          completeMeeting(state);
          state.dayStats.meetingsSurvived++;
          state.dayStats.meetingTypesThisDay.push(meeting.type);
          state.sessionStats.totalMeetingsSurvived++;
          meetingIndex++;
          playSoundEffect("meetingEnd", state.soundEnabled);
          addLogEntry(C().ui.meeting.survived(meetingTypeLabel(meeting.type)), "success");
          await delay(800);
          if (state.meetingQueue.length > 0) {
            await waitForContinue(panel, C().ui.meeting.continueToNext);
          }
        }
      } // end else (normal meeting)
    }

    if (state.isGameOver) break;

    // Day complete — award Impostor Points
    let dayPoints = IMPOSTOR_POINTS_PER_DAY;
    if (!fellAsleepThisDay) dayPoints += IMPOSTOR_POINTS_BONUS_NO_SLEEP;
    awardPoints(state.meta, dayPoints);

    state.dayStats.salaryEnd = state.player.salary;
    updatePeakSalary(state);
    state.sessionStats.totalDaysSurvived++;
    state.sessionStats.totalTimesCaught += state.dayStats.timesCaught;
    state.sessionStats.totalMinigamesWon += state.dayStats.minigamesWon;
    state.sessionStats.totalMinigamesLost += state.dayStats.minigamesLost;
    // Update best records
    if (state.player.daysSurvived + 1 > state.sessionStats.bestDaysSurvived) {
      state.sessionStats.bestDaysSurvived = state.player.daysSurvived + 1;
    }
    if (state.peakSalary > state.sessionStats.bestEndingSalary) {
      state.sessionStats.bestEndingSalary = state.peakSalary;
    }

    // Check day-end achievements
    const dayAchievements = checkAndAwardAchievements(state);
    for (const id of dayAchievements) {
      const def = achievementDefs().find((a) => a.id === id)!;
      addLogEntry(C().ui.achievements.unlocked(def.emoji, def.name, def.description), "success");
      playSoundEffect("achievement", state.soundEnabled);
    }

    playSoundEffect("daySummary", state.soundEnabled);
    clearMeetingProgress();
    showDaySummaryWithCharts(state);
    addLogEntry(C().ui.day.daySurvived(state.currentDay), "success");
    addLogEntry(C().ui.day.pointsEarned(dayPoints, !fellAsleepThisDay, state.meta.impostorPoints), "success");

    // Show newly unlocked achievements
    if (dayAchievements.length > 0) {
      addLogEntry(C().ui.day.newAchievements(dayAchievements.map((id) => { const d = achievementDefs().find((a) => a.id === id)!; return `${d.emoji} ${d.name}`; }).join(", ")), "success");
    }

    await saveGameProgress(state);
    // Save game state to localStorage for mid-session resume
    saveGameState({
      currentDay: state.currentDay,
      salary: state.player.salary,
      fatigue: state.player.fatigue,
      daysSurvived: state.player.daysSurvived,
      meetingsSurvived: state.player.meetingsSurvived,
    });
    await waitForContinue(panel, C().ui.actions.endOfDay);

    // Reset per-day tracker fields
    state.achievementTracker.monologuesToday = 0;

    advanceDay(state);
    updateStatusBar(state.player, state.meta.impostorPoints);
  }

  // Determine ending
  const endingType = determineEnding(state);
  state.ending = endingType;
  unlockEnding(state.meta, endingType);
  const endingDef = endings()[endingType];
  if (endingDef.bonusPoints > 0) {
    awardPoints(state.meta, endingDef.bonusPoints);
  }
  saveMeta(state.meta);
  clearGameState();

  playSoundEffect("gameOver", state.soundEnabled);
  screenShake("heavy");
  await showGameOverAnimation();
  showGameOver(state);
  addLogEntry(C().ui.gameOver.keptPoints(state.meta.impostorPoints, state.meta.totalPointsEarned), "normal");
  await submitGameHighscore(state);
  await saveGameProgress(state);
}

// Boot
document.addEventListener("DOMContentLoaded", () => {
  main().catch((err) => console.error("Fatal error:", err));
});
